import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

import type { BillingInterval, LicensingPlanDto } from '../../models/License';

type PlanCardProps = {
    plan: LicensingPlanDto;
    /** Billing period currently selected in the Monthly / Annual toggle. */
    interval: BillingInterval;
    isCurrent?: boolean;
    isAvailable: boolean;
    isRecommended?: boolean;
    /** This plan + interval is already scheduled for the next renewal. */
    pendingAtUtc?: string | null;
    loading?: boolean;
    onSelect: (plan: LicensingPlanDto) => void;
};

const getTierAccent = (tierCode: string) => {
    switch (tierCode.toUpperCase()) {
        case "PREMIUM":
            return {
                borderColor: "secondary.main",
                badgeColor: "secondary" as const,
            };
        case "ADVANCED":
            return {
                borderColor: "primary.main",
                badgeColor: "primary" as const,
            };
        default:
            return {
                borderColor: "divider",
                badgeColor: "default" as const,
            };
    }
};

const formatMoney = (value: number) =>
    new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(value ?? 0);

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

const FeatureRow: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
            {value}
        </Typography>
    </Box>
);

const PlanCard: React.FC<PlanCardProps> = ({
    plan,
    interval,
    isCurrent = false,
    isAvailable,
    isRecommended = false,
    pendingAtUtc,
    loading = false,
    onSelect,
}) => {
    const accent = getTierAccent(plan.tierCode);
    const isTrial = plan.tierCode.toUpperCase() === "TRIAL";
    const sellsMonthly = plan.canBuyMonthly;
    const sellsAnnual = plan.canBuyAnnual;
    const soldOnline = sellsMonthly || sellsAnnual;
    const availableForInterval = isTrial || !soldOnline || (interval === "month" ? sellsMonthly : sellsAnnual);

    // Price shown for the selected billing period; trial / manual plans keep their label.
    const price = !soldOnline || isTrial
        ? { main: plan.priceLabel ?? "Custom", per: "", note: isTrial ? "Free trial" : "Subscription plan" }
        : interval === "month"
            ? { main: formatMoney(plan.monthlyPrice), per: "/month", note: "Billed monthly · cancel any time" }
            : {
                main: formatMoney(plan.annualPrice),
                per: "/year",
                note: plan.monthlyPrice > 0
                    ? `${formatMoney(plan.annualPrice / 12)}/month equivalent · save ${formatMoney(plan.monthlyPrice * 12 - plan.annualPrice)}`
                    : "Billed annually",
            };

    const buttonLabel = isCurrent
        ? "Current Plan"
        : pendingAtUtc
            ? `Starts ${formatDate(pendingAtUtc)}`
            : !availableForInterval
                ? `Not available ${interval === "month" ? "monthly" : "annually"}`
                : loading
                    ? "Please wait..."
                    : "Select Plan";

    return (
        <Card
            sx={{
                height: "100%",
                width: "100%",
                borderWidth: 2,
                borderStyle: "solid",
                borderColor: isCurrent || isRecommended ? accent.borderColor : "divider",
                boxShadow: isCurrent || isRecommended ? 3 : 1,
                position: "relative",
                overflow: "visible", // let the badges overlap the top border
                mx: 0,
                maxWidth: "none",
            }}
        >
            {(isRecommended || isCurrent || pendingAtUtc) && (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ position: "absolute", top: -12, left: 0, right: 0, justifyContent: "center", zIndex: 1 }}
                >
                    {isCurrent && <Chip label="Current" color="success" size="small" />}
                    {isRecommended && <Chip label="Recommended" color={accent.badgeColor} size="small" />}
                    {pendingAtUtc && (
                        <Chip label="Scheduled" color="info" size="small" sx={{ bgcolor: "background.paper" }} variant="outlined" />
                    )}
                </Stack>
            )}

            <CardContent sx={{ height: "100%" }}>
                <Stack spacing={2} sx={{ height: "100%" }}>
                    {/* Heading block reserves room for a two-line tier name, so prices and limits line up
                        across cards whether a name fits on one line or wraps. */}
                    <Box sx={{ minHeight: "4.6rem" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                lineHeight: 1.3,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                            title={plan.tierName}
                        >
                            {plan.tierName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {plan.planName}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="h5" fontWeight={700} component="span">
                            {price.main}
                        </Typography>
                        {price.per && (
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                                {price.per}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                            {price.note}
                        </Typography>
                    </Box>

                    <Divider />

                    <Stack spacing={1.25}>
                        <FeatureRow label="Collections" value={plan.maxClients} />
                        <FeatureRow label="Portfolios" value={plan.maxPortfolios} />
                        <FeatureRow label="Artworks" value={plan.maxArtworks} />
                    </Stack>

                    <Box flexGrow={1} />

                    <Button
                        variant={isCurrent ? "outlined" : "contained"}
                        color={isCurrent ? "inherit" : accent.badgeColor === "default" ? "primary" : accent.badgeColor}
                        fullWidth
                        disabled={isCurrent || loading || !isAvailable || !availableForInterval || !!pendingAtUtc}
                        onClick={() => onSelect(plan)}
                    >
                        {buttonLabel}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PlanCard;
